import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUpRight, X, ChevronLeft, ChevronRight } from 'lucide-react';

const pieces = [
    {
        // Big hero tile — widest, most impactful shot
        src: "/Marble%20polish/banner/67011d89e8a31_Banner%20-%20-Marble-Flooring-For-Home.jpg",
        category: "Marble Polishing",
        label: "Our Work For A Client",
        area: "a",
    },
    {
        // Ceiling — ornate dome & chandelier
        src: "/Ceiling/10.webp",
        category: "Ceiling Works",
        label: "Our Work For A Client",
        area: "b",
    },
    {
        // Joinery — wide office showroom
        src: "/Joinery/5.jpeg",
        category: "Joinery & Carpentry",
        label: "Our Work For A Client",
        area: "c",
    },
    {
        // Marble installation — dramatic blue marble bathroom
        src: "/Marble%20polish/m3.jpg",
        category: "Tile & Marble Installation",
        label: "Our Work For A Client",
        area: "d",
    },
    {
        // Joinery — dark walnut wall panels + marble feature wall
        src: "/Joinery/11.jpeg",
        category: "Joinery & Carpentry",
        label: "Our Work For A Client",
        area: "e",
    },
    {
        // Ceiling — warm glowing tray ceiling in bedroom
        src: "/Ceiling/2.jpg",
        category: "Ceiling Works",
        label: "Our Work For A Client",
        area: "f",
    },
    {
        // Joinery — coffee bar with marble arch & wood slats
        src: "/Joinery/9.jpeg",
        category: "Joinery & Carpentry",
        label: "Our Work For A Client",
        area: "g",
    },
];


/* ─── Lightbox ───────────────────────────────────────────────────── */
const Lightbox = ({ index, onClose, onPrev, onNext }) => {
    const piece = pieces[index];

    // keyboard navigation
    useEffect(() => {
        const handler = (e) => {
            if (e.key === 'Escape') onClose();
            if (e.key === 'ArrowLeft') onPrev();
            if (e.key === 'ArrowRight') onNext();
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [onClose, onPrev, onNext]);

    // lock body scroll
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = ''; };
    }, []);

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center"
            style={{ background: 'rgba(0,0,0,0.94)', backdropFilter: 'blur(8px)' }}
            onClick={onClose}
        >
            {/* Close button */}
            <button
                onClick={onClose}
                className="absolute top-5 right-5 w-10 h-10 rounded-full flex items-center justify-center text-white border border-white/20 hover:border-yellow-400 hover:text-yellow-400 transition-colors z-10"
            >
                <X size={20} />
            </button>

            {/* Prev */}
            <button
                onClick={(e) => { e.stopPropagation(); onPrev(); }}
                className="absolute left-4 md:left-8 w-12 h-12 rounded-full flex items-center justify-center text-white border border-white/20 hover:border-yellow-400 hover:text-yellow-400 transition-colors z-10"
            >
                <ChevronLeft size={24} />
            </button>

            {/* Image container — stop propagation so clicking the image doesn't close */}
            <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.93 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.93 }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
                className="relative flex flex-col items-center max-w-5xl w-full mx-20 md:mx-28"
                onClick={(e) => e.stopPropagation()}
            >
                <img
                    src={piece.src}
                    alt={piece.label}
                    className="w-full max-h-[80vh] object-contain rounded shadow-2xl"
                    style={{ boxShadow: '0 0 80px rgba(212,175,55,0.15)' }}
                />
                {/* Caption */}
                <div className="mt-4 text-center">
                    <p className="text-yellow-400 uppercase tracking-widest text-xs mb-1">{piece.category}</p>
                    <p className="text-white font-serif text-lg">{piece.label}</p>
                    <p className="text-gray-600 text-xs mt-1">{index + 1} / {pieces.length}</p>
                </div>
            </motion.div>

            {/* Next */}
            <button
                onClick={(e) => { e.stopPropagation(); onNext(); }}
                className="absolute right-4 md:right-8 w-12 h-12 rounded-full flex items-center justify-center text-white border border-white/20 hover:border-yellow-400 hover:text-yellow-400 transition-colors z-10"
            >
                <ChevronRight size={24} />
            </button>

            {/* Dot indicators */}
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
                {pieces.map((_, i) => (
                    <button
                        key={i}
                        onClick={(e) => { e.stopPropagation(); /* jump to i */ }}
                        className="w-1.5 h-1.5 rounded-full transition-all duration-300"
                        style={{ background: i === index ? '#D4AF37' : 'rgba(255,255,255,0.25)' }}
                    />
                ))}
            </div>
        </motion.div>
    );
};

/* ─── Single tile ────────────────────────────────────────────────── */
const Tile = ({ piece, tileIndex, delay, onClick }) => {
    const [hovered, setHovered] = useState(false);
    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.65, delay }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onClick={() => onClick(tileIndex)}
            className="relative overflow-hidden cursor-pointer"
            style={{ gridArea: piece.area }}
        >
            <img
                src={piece.src}
                alt={piece.label}
                className="w-full h-full object-cover transition-transform duration-700"
                style={{ transform: hovered ? 'scale(1.07)' : 'scale(1)' }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />

            {/* label */}
            <div className="absolute bottom-0 left-0 right-0 p-4">
                <p
                    className="text-xs uppercase tracking-widest mb-1 transition-colors duration-300"
                    style={{ color: hovered ? '#D4AF37' : 'rgba(212,175,55,0.7)' }}
                >
                    {piece.category}
                </p>
                <p className="text-white text-sm font-medium leading-tight">{piece.label}</p>
            </div>

            {/* hover overlay */}
            <motion.div
                animate={{ opacity: hovered ? 1 : 0 }}
                transition={{ duration: 0.3 }}
                className="absolute inset-0 flex items-center justify-center"
                style={{ background: 'rgba(212,175,55,0.12)', backdropFilter: 'blur(2px)' }}
            >
                <div
                    className="w-12 h-12 rounded-full border-2 border-white flex items-center justify-center"
                    style={{ background: 'rgba(0,0,0,0.35)' }}
                >
                    <ArrowUpRight size={20} color="white" />
                </div>
            </motion.div>
        </motion.div>
    );
};

/* ─── WorkGallery ────────────────────────────────────────────────── */
const WorkGallery = () => {
    const [lightboxIndex, setLightboxIndex] = useState(null);

    const openLightbox = useCallback((i) => setLightboxIndex(i), []);
    const closeLightbox = useCallback(() => setLightboxIndex(null), []);
    const prevImage = useCallback(() => setLightboxIndex((i) => (i - 1 + pieces.length) % pieces.length), []);
    const nextImage = useCallback(() => setLightboxIndex((i) => (i + 1) % pieces.length), []);

    return (
        <section id="work" className="bg-black py-12 md:py-20">
            {/* Header */}
            <div className="max-w-7xl mx-auto px-5 md:px-6 mb-8 md:mb-10 flex flex-col md:flex-row md:items-end md:justify-between gap-3 md:gap-4">
                <div>
                    <p className="text-yellow-500 uppercase tracking-[0.3em] text-xs font-semibold mb-2">Portfolio</p>
                    <h2 className="font-serif text-3xl sm:text-5xl text-white leading-tight">
                        Our <em className="not-italic" style={{ color: '#D4AF37' }}>Masterpieces</em>
                    </h2>
                </div>
                <p className="text-gray-500 max-w-sm text-sm leading-relaxed">
                    A curated glimpse into the luxurious spaces we've had the privilege to transform across the UAE.
                    <span className="block mt-1 text-yellow-600/70">Tap any image to view full size.</span>
                </p>
            </div>

            {/* Grid — 2-col on mobile, fancy collage on md+ */}
            <div className="max-w-7xl mx-auto px-5 md:px-6">
                {/* Mobile: 2-column uniform grid */}
                <div className="md:hidden grid grid-cols-2 gap-1 rounded-sm overflow-hidden">
                    {pieces.map((p, i) => (
                        <div
                            key={i}
                            className="relative overflow-hidden cursor-pointer"
                            style={{ height: i === 0 ? '240px' : '160px' }}
                            onClick={() => openLightbox(i)}
                        >
                            <img
                                src={p.src}
                                alt={p.label}
                                className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                            <div className="absolute bottom-0 left-0 right-0 p-2.5">
                                <p className="text-[10px] uppercase tracking-widest mb-0.5" style={{ color: '#D4AF37' }}>{p.category}</p>
                                <p className="text-white text-xs font-medium leading-tight">{p.label}</p>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Desktop: fancy collage grid */}
                <div
                    className="hidden md:grid w-full rounded-sm overflow-hidden"
                    style={{
                        gridTemplateColumns: '2fr 1fr 1fr 1fr',
                        gridTemplateRows: '300px 300px 240px',
                        gridTemplateAreas: `
              "a a b c"
              "a a d e"
              "f g g g"
            `,
                        gap: '4px',
                    }}
                >
                    {pieces.map((p, i) => (
                        <Tile key={i} piece={p} tileIndex={i} delay={i * 0.06} onClick={openLightbox} />
                    ))}
                </div>
            </div>

            {/* CTA */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-center mt-12"
            >
                <a
                    href="#contact"
                    className="inline-flex items-center gap-2 text-sm uppercase tracking-widest px-8 py-4 border border-yellow-500 text-yellow-400 hover:bg-yellow-500 hover:text-black transition-all duration-300 font-semibold"
                >
                    Start Your Project
                    <ArrowUpRight size={16} />
                </a>
            </motion.div>

            {/* Lightbox */}
            <AnimatePresence>
                {lightboxIndex !== null && (
                    <Lightbox
                        index={lightboxIndex}
                        onClose={closeLightbox}
                        onPrev={prevImage}
                        onNext={nextImage}
                    />
                )}
            </AnimatePresence>
        </section>
    );
};

export default WorkGallery;
